import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsScaffoldConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';
import {
    buildVisitorsSummaryColumnAggregates,
    getCurrentPage,
    getNameLabel,
    getPerPage,
    getTotal,
    normalizeVisitorsRows,
} from '@client/app/feature-modules/site-analytics/definitions/visitors-definition.utils';

const VISITORS_DEVICES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.visitors.devices.header',
    pageSubheaderKey: 'siteAnalytics.visitors.devices.subheader',
    tableKey: 'site-analytics-visitors-devices',
    dataSource: 'visitorsDevices',
    exportCategory: 'visitors_devices',
    searchSupported: false,
    searchPlaceholderKey: 'siteAnalytics.visitors.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.visitors.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.visitors.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.visitors.devices.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.visitors.devices.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    columns: [
        {
            id: 'name',
            headerKey: 'siteAnalytics.scaffold.table.column.name',
            displayType: 'text',
            width: 'xl',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'actionsPerVisit',
            headerKey: 'siteAnalytics.scaffold.table.column.actionsPerVisit',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            width: 'md',
        },
    ],
};

const loadVisitorsDevicesData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const params = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
    });

    const [devicesResponse, summaryResponse] = await Promise.all([
        statsRepoService.getDevices(params, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getSummary({
            domainId: request.domainId,
            from: params.from,
            to: params.to,
            type: 'total',
        }, contextTokens.NO_PARSE_SEARCH),
    ]);
    const rows = normalizeVisitorsRows(devicesResponse).filter((row) => toNumber(row.visits) > 0);

    return {
        rows,
        chart: {
            labels: rows.map((row) => getNameLabel(row)),
            primaryValues: rows.map((row) => toNumber(row.visits)),
        },
        summary: summaryResponse,
        columnAggregates: buildVisitorsSummaryColumnAggregates(summaryResponse),
        total: getTotal(devicesResponse, rows.length),
        perPage: getPerPage(devicesResponse, request.perPage),
        page: getCurrentPage(devicesResponse, request.page),
    };
};

function toNumber (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const VISITORS_DEVICES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'visitorsDevices',
    config: VISITORS_DEVICES_CONFIG,
    loadData: loadVisitorsDevicesData,
};

export const VISITORS_DEVICES_PAGE_CONFIG = VISITORS_DEVICES_DEFINITION.config;
